<script setup lang="ts">
import { reactive } from 'vue';
import { useDialogPluginComponent, copyToClipboard, Notify } from 'quasar';
import {
  MESSAGE_VALIDATION_ISSUE_LABELS,
  MessageGateAction,
  type MessageReviewSection,
  type MessageValidationIssue,
} from './ticketsTypes.ts';

const props = defineProps<{
  sections: MessageReviewSection[];
}>();

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

type Choice = 'improved' | 'original';

// Per-field choice, defaulting to the improved suggestion.
const choices = reactive<Record<string, Choice>>(
  Object.fromEntries(props.sections.map((s) => [s.key, 'improved'])),
);

const choiceOptions = [
  { label: 'Use suggested', value: 'improved' },
  { label: 'Keep mine', value: 'original' },
];

const issueLabelsFor = (section: MessageReviewSection): string[] =>
  section.issues.map((code: MessageValidationIssue) => MESSAGE_VALIDATION_ISSUE_LABELS[code] ?? code);

const translationLabelFor = (section: MessageReviewSection): string =>
  section.translationLanguage?.trim()
    ? `What it means (${section.translationLanguage})`
    : 'What it means';

// Apply the per-field choice and return the resolved text for every section.
const applyChoices = (): void => {
  const texts: Record<string, string> = {};
  for (const section of props.sections) {
    texts[section.key] = choices[section.key] === 'original' ? section.original : section.improvedEn;
  }
  onDialogOK({ action: MessageGateAction.Send, texts });
};

// Closing keeps the accountant's original text so they can edit the form themselves.
const closeDialog = (): void => onDialogCancel();

const copyText = async (text: string): Promise<void> => {
  try {
    await copyToClipboard(text);
    Notify.create({ message: 'Copied to clipboard', color: 'positive', position: 'top' });
  } catch {
    Notify.create({ message: 'Failed to copy', color: 'negative', position: 'top' });
  }
};
</script>

<template>
  <q-dialog
    ref="dialogRef"
    @hide="onDialogHide"
  >
    <q-card class="create-review-card column no-wrap">
      <q-card-section class="review-header row items-center justify-between">
        <div class="text-h6">
          Review before creating
        </div>
        <q-btn
          icon="close"
          color="primary"
          flat
          round
          dense
          @click="closeDialog"
        >
          <q-tooltip
            class="bg-grey-10 text-body1"
            max-width="280px"
          >
            Close and keep editing the form yourself. Nothing is created.
          </q-tooltip>
        </q-btn>
      </q-card-section>

      <q-separator />

      <q-card-section class="review-body col scroll column no-wrap">
        <template
          v-for="(section, index) in sections"
          :key="section.key"
        >
          <q-separator
            v-if="index > 0"
            class="q-my-lg"
          />
          <div>
            <div class="text-subtitle2 text-weight-bold q-mb-sm">
              {{ section.label }}
            </div>

            <div
              v-if="issueLabelsFor(section).length || section.foreignFragments.length"
              class="q-mb-sm"
            >
              <div class="row items-center q-gutter-xs">
                <q-chip
                  v-for="label in issueLabelsFor(section)"
                  :key="label"
                  color="orange-2"
                  text-color="orange-9"
                  dense
                  size="sm"
                  icon="warning"
                >
                  {{ label }}
                </q-chip>
                <!-- The exact words the AI found in another language. -->
                <q-chip
                  v-for="(fragment, fragmentIndex) in section.foreignFragments"
                  :key="`${fragment}-${fragmentIndex}`"
                  color="grey-3"
                  text-color="grey-9"
                  dense
                  size="sm"
                >
                  {{ fragment }}
                </q-chip>
              </div>
              <div
                v-if="section.foreignFragments.length"
                class="text-caption text-grey-7 q-mt-xs"
              >
                The client cannot read these words. They are translated in the suggestion below.
              </div>
            </div>

            <!-- Original -->
            <div class="text-caption text-grey-7 q-mb-xs">
              Your version
            </div>
            <div class="review-block review-block--muted q-mb-sm">
              {{ section.original }}
            </div>

            <!-- Suggested English -->
            <div class="row items-center justify-between q-mb-xs">
              <div class="text-caption text-primary text-weight-medium">
                Suggested (English)
              </div>
              <q-btn
                icon="content_copy"
                flat
                round
                dense
                size="sm"
                color="primary"
                @click="copyText(section.improvedEn)"
              >
                <q-tooltip
                  class="bg-grey-10 text-body1"
                  max-width="280px"
                >
                  Copy the English version to the clipboard.
                </q-tooltip>
              </q-btn>
            </div>
            <div class="review-block review-block--primary">
              {{ section.improvedEn }}
            </div>

            <!-- Back-translation into the source language -->
            <template v-if="section.translation?.trim()">
              <div class="text-caption text-grey-7 q-mt-sm q-mb-xs">
                {{ translationLabelFor(section) }}
              </div>
              <div class="review-block review-block--translation">
                {{ section.translation }}
              </div>
            </template>

            <q-option-group
              v-model="choices[section.key]"
              :options="choiceOptions"
              color="secondary"
              inline
              dense
              class="q-mt-md"
            />
          </div>
        </template>
      </q-card-section>

      <q-separator />

      <q-card-actions
        align="right"
        class="review-footer q-gutter-sm"
      >
        <q-btn
          label="Edit myself"
          icon="edit"
          flat
          no-caps
          @click="closeDialog"
        >
          <q-tooltip
            class="bg-grey-10 text-body1"
            max-width="280px"
          >
            Go back to the form to edit the fields yourself. Nothing is created yet.
          </q-tooltip>
        </q-btn>
        <q-btn
          label="Apply & continue"
          icon="auto_awesome"
          unelevated
          no-caps
          color="secondary"
          @click="applyChoices"
        >
          <q-tooltip
            class="bg-grey-10 text-body1"
            max-width="280px"
          >
            Use the selected version for each field and create the question.
          </q-tooltip>
        </q-btn>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<style scoped>
.create-review-card {
  width: 680px;
  max-width: 92vw;
  max-height: 85vh;
}

/* Fixed header/footer with an internally scrolling body keeps content
   from riding over the title while scrolling. */
.review-header {
  padding: 20px 24px;
}

.review-body {
  padding: 20px 24px;
}

.review-footer {
  padding: 16px 24px;
}

.review-block {
  white-space: pre-wrap;
  word-break: break-word;
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.55;
}

.review-block--muted {
  background: #f5f5f5;
  color: #616161;
}

.review-block--primary {
  background: #e8eaf6;
  color: #1a237e;
  font-weight: 500;
}

.review-block--translation {
  background: #f1f8e9;
  color: #33691e;
}
</style>
